/**
 * Bezier approximations for the two Phira curves RPE has no easingType for: InOutQuint (tween 17) and InOutExpo
 * (tween 20) (D§4.12: "offered as a fitted Bezier with its error shown").
 *
 * Both curves are point-symmetric about (0.5, 0.5), so the fit is the symmetric cubic bezier
 * (x1, y1, 1 - x1, 1 - y1) minimising the squared error to Phira's curve on 64 samples x = k / 63, with the bezier
 * evaluated by Phira's own solver (what the exported curve will look like). The minimiser is a deterministic
 * Nelder-Mead search with x1 and y1 kept in [0, 1]: the originals never leave [0, 1], so neither may the fit (an
 * unconstrained fit dips below 0, which would make it an overshooting curve, refused on text reveal / erase).
 * The handles are then rounded to 4 decimals (clean JSON) and the reported maxError is the largest
 * |bezier - curve| on a dense grid of 1025 points with the rounded handles.
 * Results are computed once per curve and cached.
 */

import { createBezierCurve } from './bezier';
import { complementUnit } from './reverse';
import { staticY, tweenIdOf } from './tweens';
import type { BezierPoints } from './types';

const FIT_SAMPLES = 64;
const ERROR_SAMPLES = 1024;

export interface BezierApprox {
  readonly p: BezierPoints;
  readonly maxError: number;
}

function symmetric(x1: number, y1: number): BezierPoints {
  return [x1, y1, complementUnit(x1), complementUnit(y1)];
}

function clamp01(v: number): number {
  return Math.min(Math.max(v, 0), 1);
}

function outside01(v: number): number {
  return v < 0 ? -v : v > 1 ? v - 1 : 0;
}

function objective(target: (x: number) => number, x1: number, y1: number): number {
  const cx = clamp01(x1);
  const cy = clamp01(y1);
  const curve = createBezierCurve([cx, cy, 1 - cx, 1 - cy]);
  let sum = 0;
  for (let k = 0; k < FIT_SAMPLES; k++) {
    const x = k / (FIT_SAMPLES - 1);
    const d = curve.y(x) - target(x);
    sum += d * d;
  }
  return sum + (outside01(x1) + outside01(y1)) * 1e3;
}

type Vertex = readonly [x: number, y: number, f: number];

function nelderMead(f: (x: number, y: number) => number, start: readonly [number, number]): readonly [number, number] {
  const make = (x: number, y: number): Vertex => [x, y, f(x, y)];
  let simplex: Vertex[] = [make(start[0], start[1]), make(start[0] + 0.1, start[1]), make(start[0], start[1] + 0.1)];
  for (let iter = 0; iter < 400; iter++) {
    simplex.sort((a, b) => a[2] - b[2]);
    const [best, mid, worst] = simplex as [Vertex, Vertex, Vertex];
    if (Math.abs(worst[2] - best[2]) < 1e-16 && Math.abs(worst[0] - best[0]) + Math.abs(worst[1] - best[1]) < 1e-10)
      break;
    const cx = (best[0] + mid[0]) / 2;
    const cy = (best[1] + mid[1]) / 2;
    const reflected = make(cx + (cx - worst[0]), cy + (cy - worst[1]));
    if (reflected[2] < best[2]) {
      const expanded = make(cx + 2 * (cx - worst[0]), cy + 2 * (cy - worst[1]));
      simplex = [best, mid, expanded[2] < reflected[2] ? expanded : reflected];
    } else if (reflected[2] < mid[2]) {
      simplex = [best, mid, reflected];
    } else {
      const contracted = make(cx + 0.5 * (worst[0] - cx), cy + 0.5 * (worst[1] - cy));
      if (contracted[2] < worst[2]) simplex = [best, mid, contracted];
      else {
        simplex = [
          best,
          make(best[0] + 0.5 * (mid[0] - best[0]), best[1] + 0.5 * (mid[1] - best[1])),
          make(best[0] + 0.5 * (worst[0] - best[0]), best[1] + 0.5 * (worst[1] - best[1])),
        ];
      }
    }
  }
  simplex.sort((a, b) => a[2] - b[2]);
  const best = simplex[0] ?? make(start[0], start[1]);
  return [best[0], best[1]];
}

function maxErrorOf(p: BezierPoints, target: (x: number) => number): number {
  const curve = createBezierCurve(p);
  let max = 0;
  for (let k = 0; k <= ERROR_SAMPLES; k++) {
    const x = k / ERROR_SAMPLES;
    max = Math.max(max, Math.abs(curve.y(x) - target(x)));
  }
  return max;
}

function round4(v: number): number {
  return Math.round(v * 1e4) / 1e4;
}

function fit(family: 'quint' | 'expo'): BezierApprox {
  const tween = tweenIdOf(family, 'inout');
  const target = (x: number): number => staticY(tween, x);
  const [x1, y1] = nelderMead((x, y) => objective(target, x, y), [0.8, 0.05]);
  const p = symmetric(round4(clamp01(x1)), round4(clamp01(y1)));
  return { p, maxError: maxErrorOf(p, target) };
}

const cache = new Map<'quint' | 'expo', BezierApprox>();

/** Best symmetric bezier for Phira's InOutQuint / InOutExpo (cached). Only the InOut variants lack an RPE id. */
export function approxBezier(family: 'quint' | 'expo', _variant: 'inout'): BezierApprox {
  let result = cache.get(family);
  if (result === undefined) {
    result = fit(family);
    cache.set(family, result);
  }
  return result;
}
